import { ColorScheme } from '@unifyapps/defs/types/interface';
import { useTranslation } from '@unifyapps/i18n/client';
import ChevronRight from '@unifyapps/icons/outline/ChevronRight';
import Monitor01 from '@unifyapps/icons/outline/Monitor01';
import Moon01 from '@unifyapps/icons/outline/Moon01';
import Sun from '@unifyapps/icons/outline/Sun';
import { Popover } from '@unifyapps/ui/components/Popover';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';
import { EMPTY_OBJECT } from '../consts/empty';
import useSetColorScheme from '../no-code/hooks/useGlobalBlockActions/actions/setColorScheme/useSetColorScheme';
import { useGlobalAppStateStore } from '../no-code/stores/app/GlobalAppStateStore';

function ColorSchemeOption({
  label,
  Icon,
  isActive,
  onSelect,
  value,
}: {
  value: ColorScheme;
  label: string;
  Icon: React.ElementType;
  isActive: boolean;
  onSelect: (value: ColorScheme) => void;
}) {
  return (
    <Stack
      alignItems="center"
      className={clsx(
        'gap-x-md cursor-pointer rounded-lg px-md py-sm transition-colors hover:bg-primary_hover',
        isActive ? '!bg-brand-primary' : '',
      )}
      direction="row"
      onClick={() => onSelect(value)}
    >
      <Icon className="shrink-0 text-primary" height={16} width={16} />
      <Typography className="flex-1" color="text-primary" variant="text-sm" weight="light">
        {label}
      </Typography>
    </Stack>
  );
}

export function ColorSchemeSwitcher({ direction }: { direction?: 'rtl' | 'ltr' }) {
  const { t } = useTranslation(['blocks', 'interfaces-matrix', 'common']);

  const currentScheme = useGlobalAppStateStore().hooks.useColorSchemeDetails().originalColorScheme;

  const schemeOptions = useMemo(
    () => [
      { value: ColorScheme.LIGHT, label: t('interfaces-matrix:ColorScheme.Light'), Icon: Sun },
      { value: ColorScheme.DARK, label: t('interfaces-matrix:ColorScheme.Dark'), Icon: Moon01 },
      { value: ColorScheme.SYSTEM, label: t('common:System'), Icon: Monitor01 },
    ],
    [t],
  );

  const ActiveIcon = schemeOptions.find((o) => o.value === currentScheme)?.Icon ?? Sun;

  const { onSetColorScheme } = useSetColorScheme();

  const onSelect = useCallback(
    (colorScheme: ColorScheme) => {
      void onSetColorScheme({
        action: { payload: { theme: colorScheme } },
        actionContext: EMPTY_OBJECT,
      });
    },
    [onSetColorScheme],
  );

  return (
    <Popover triggerType="hover">
      <Popover.Button>
        <Stack
          alignItems="center"
          className="hover:bg-secondary_hover py-md ps-sm pe-sm mx-lg my-sm cursor-pointer rounded-sm gap-x-md"
          direction="row"
        >
          <ActiveIcon className="text-icon-secondary" height={20} width={20} />
          <Typography className="flex-1" color="text-secondary" variant="text-sm" weight="medium">
            {t('blocks:Navigation.ChangeTheme')}
          </Typography>
          <ChevronRight className="text-icon-tertiary" height={16} width={16} />
        </Stack>
      </Popover.Button>
      <Popover.Content
        minWidth={15}
        offset={4}
        placement={direction === 'rtl' ? 'left-start' : 'right-start'}
      >
        <Stack className="gap-y-xxs p-xs" direction="column">
          {schemeOptions.map(({ value, label, Icon }) => (
            <ColorSchemeOption
              Icon={Icon}
              isActive={currentScheme === value}
              key={value}
              label={label}
              onSelect={onSelect}
              value={value}
            />
          ))}
        </Stack>
      </Popover.Content>
    </Popover>
  );
}
